/**
 * Pure Firebase-Storage-download-URL parsing, deliberately kept free of any
 * DOM global (navigator, window, document). saveAttachment.ts (the client's
 * own render-path sanitizers) imports this; so does
 * netlify/functions/lib/videoStorageReconcile.mjs (security-audit-2026-09-16
 * finding H-2 round 2, P1-A), and a Netlify function's own tsconfig has no
 * "dom" lib, so pulling this parser out of a DOM-heavy file is what makes
 * that second import typecheck at all - split for a build reason, not a
 * design one.
 */

const HUB_STORAGE_BUCKETS = new Set([
  // Firebase renamed new default buckets from appspot.com to
  // firebasestorage.app. Existing Hub media can legitimately live in either
  // form, but never in another Firebase project's bucket.
  'ldg-helper.firebasestorage.app',
  'ldg-helper.appspot.com',
]);

export interface SafeHubStorageDownload {
  url: string;
  storagePath: string;
}

/** Parse the one Firebase download-URL shape the Hub is willing to request. */
export function safeHubStorageDownloadUrl(rawUrl: unknown): SafeHubStorageDownload | null {
  if (typeof rawUrl !== 'string' || rawUrl.length > 4096) return null;
  let parsed: URL;
  try {
    parsed = new URL(rawUrl);
  } catch {
    return null;
  }

  if (
    parsed.protocol !== 'https:'
    || parsed.hostname !== 'firebasestorage.googleapis.com'
    || parsed.port !== ''
    || parsed.username !== ''
    || parsed.password !== ''
    || parsed.hash !== ''
  ) {
    return null;
  }

  const match = /^\/v0\/b\/([^/]+)\/o\/([^/]+)$/.exec(parsed.pathname);
  if (!match) return null;

  let bucket: string;
  let storagePath: string;
  try {
    bucket = decodeURIComponent(match[1]);
    storagePath = decodeURIComponent(match[2]);
  } catch {
    return null;
  }
  if (
    !HUB_STORAGE_BUCKETS.has(bucket)
    || !storagePath
    || storagePath.length > 1024
    || storagePath.startsWith('/')
    || storagePath.includes('\\')
    || /[\u0000-\u001f\u007f]/.test(storagePath)
    || storagePath.split('/').some((part) => part === '' || part === '.' || part === '..')
  ) {
    return null;
  }

  const queryKeys = [...parsed.searchParams.keys()];
  if (
    queryKeys.length !== 2
    || queryKeys.some((key) => key !== 'alt' && key !== 'token')
    || parsed.searchParams.getAll('alt').length !== 1
    || parsed.searchParams.get('alt') !== 'media'
    || parsed.searchParams.getAll('token').length !== 1
  ) {
    return null;
  }
  const token = parsed.searchParams.get('token') ?? '';
  if (!token || token.length > 512 || /[\u0000-\u001f\u007f]/.test(token)) return null;

  return { url: parsed.toString(), storagePath };
}
